import { useState } from 'react';
import type { Lead, LeadStatus } from '../../../../mocks/crm';
import { agencyInfo } from '../../../../mocks/dashboard';

const brand = agencyInfo.brandColor;

const statusColors: Record<LeadStatus, { bg: string; text: string; dot: string }> = {
  New: { bg: 'bg-teal-50', text: 'text-teal-700', dot: 'bg-teal-500' },
  Contacted: { bg: 'bg-violet-50', text: 'text-violet-700', dot: 'bg-violet-500' },
  Quoted: { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
  Negotiating: { bg: 'bg-orange-50', text: 'text-orange-700', dot: 'bg-orange-500' },
  Won: { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  Lost: { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
};

const sourceIconMap: Record<string, string> = {
  WhatsApp: 'ri-whatsapp-line',
  Website: 'ri-global-line',
  Phone: 'ri-phone-line',
  Email: 'ri-mail-line',
  Referral: 'ri-user-heart-line',
  'Walk-in': 'ri-store-line',
};

interface LeadsTableProps {
  leads: Lead[];
  onLeadClick: (lead: Lead) => void;
}

type SortKey = 'name' | 'service' | 'budget' | 'status' | 'source' | 'createdAt';

export default function LeadsTable({ leads, onLeadClick }: LeadsTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const perPage = 10;

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(1);
  };

  const sorted = [...leads].sort((a, b) => {
    let av: string | number = a[sortKey as keyof Lead] as string | number;
    let bv: string | number = b[sortKey as keyof Lead] as string | number;
    if (sortKey === 'budget') { av = a.budgetNum; bv = b.budgetNum; }
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const paginated = sorted.slice((page - 1) * perPage, page * perPage);
  const totalPages = Math.ceil(sorted.length / perPage);

  const SortIcon = ({ col }: { col: SortKey }) => (
    <i
      className={`ml-1 text-xs ${
        sortKey === col
          ? sortDir === 'asc'
            ? 'ri-arrow-up-s-fill text-gray-700'
            : 'ri-arrow-down-s-fill text-gray-700'
          : 'ri-arrow-up-down-line text-gray-400'
      }`}
    />
  );

  const thClass = 'px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider select-none cursor-pointer hover:text-gray-700 whitespace-nowrap';

  return (
    <div>
      <div className="overflow-x-auto rounded-2xl border border-gray-100">
        <table className="w-full">
          <thead className="bg-gray-50/80">
            <tr>
              <th className={thClass} onClick={() => handleSort('name')}>
                Lead <SortIcon col="name" />
              </th>
              <th className={thClass} onClick={() => handleSort('service')}>
                Service <SortIcon col="service" />
              </th>
              <th className={thClass} onClick={() => handleSort('budget')}>
                Budget <SortIcon col="budget" />
              </th>
              <th className={thClass} onClick={() => handleSort('status')}>
                Status <SortIcon col="status" />
              </th>
              <th className={thClass} onClick={() => handleSort('source')}>
                Source <SortIcon col="source" />
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Assigned
              </th>
              <th className={thClass} onClick={() => handleSort('createdAt')}>
                Created <SortIcon col="createdAt" />
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-50">
            {paginated.map((lead) => {
              const sc = statusColors[lead.status];
              return (
                <tr
                  key={lead.id}
                  onClick={() => onLeadClick(lead)}
                  className="hover:bg-gray-50/60 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                        style={{ backgroundColor: brand }}
                      >
                        {lead.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-800 whitespace-nowrap">{lead.name}</p>
                        <p className="text-xs text-gray-400">{lead.phone}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-1.5">
                      <i className={`${lead.serviceIcon} text-sm text-gray-400`} />
                      <span className="text-sm text-gray-700 whitespace-nowrap max-w-[160px] truncate block">{lead.service}</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">{lead.pax} pax · {lead.travelDate}</p>
                  </td>
                  <td className="px-4 py-3.5">
                    <span className="text-sm font-semibold text-gray-800 whitespace-nowrap">{lead.budget}</span>
                  </td>
                  <td className="px-4 py-3.5">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${sc.bg} ${sc.text}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                      {lead.status}
                    </span>
                  </td>
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-1.5 text-gray-600">
                      <i className={`${sourceIconMap[lead.source]} text-sm`} />
                      <span className="text-sm whitespace-nowrap">{lead.source}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                        style={{ backgroundColor: brand }}
                      >
                        {lead.assignedAvatar}
                      </div>
                      <span className="text-sm text-gray-600 whitespace-nowrap">{lead.assignedTo}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3.5">
                    <span className="text-sm text-gray-500 whitespace-nowrap">{lead.createdAt}</span>
                  </td>
                  <td className="px-4 py-3.5" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); onLeadClick(lead); }}
                        className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 cursor-pointer transition-colors"
                      >
                        <i className="ri-eye-line text-sm" />
                      </button>
                      <button className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 cursor-pointer transition-colors">
                        <i className="ri-edit-line text-sm" />
                      </button>
                      <button className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 cursor-pointer transition-colors">
                        <i className="ri-delete-bin-line text-sm" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 px-1">
          <p className="text-sm text-gray-500">
            Showing {(page - 1) * perPage + 1}–{Math.min(page * perPage, sorted.length)} of {sorted.length} leads
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
            >
              <i className="ri-arrow-left-s-line text-sm" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                onClick={() => setPage(p)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium cursor-pointer transition-colors"
                style={
                  p === page
                    ? { backgroundColor: brand, color: 'white' }
                    : { color: '#6b7280' }
                }
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
            >
              <i className="ri-arrow-right-s-line text-sm" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
